import {
  ExpectedCountDayKey,
  ExpectedCountMode,
  ScheduleDayCounts,
  ScheduleExpectedCountWeek,
} from '../types';

export const EXPECTED_COUNT_DAY_LABELS: { key: ExpectedCountDayKey; label: string; short: string }[] = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' },
  { key: 'bankHoliday', label: 'BH', short: 'BH' },
];

export function emptyDayCounts(): ScheduleDayCounts {
  return {
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
    bankHoliday: 0,
  };
}

export function normalizeDayCounts(raw?: Partial<ScheduleDayCounts> | null): ScheduleDayCounts {
  const empty = emptyDayCounts();
  if (!raw) return empty;
  return {
    monday: Number(raw.monday) || 0,
    tuesday: Number(raw.tuesday) || 0,
    wednesday: Number(raw.wednesday) || 0,
    thursday: Number(raw.thursday) || 0,
    friday: Number(raw.friday) || 0,
    saturday: Number(raw.saturday) || 0,
    sunday: Number(raw.sunday) || 0,
    bankHoliday: Number(raw.bankHoliday) || 0,
  };
}

export function shiftDateKey(startTime: string): string {
  return (startTime || '').slice(0, 10);
}

const WEEKDAY_KEYS: ExpectedCountDayKey[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export function isBankHolidayDate(date: string, publicHolidayDates: string[]): boolean {
  const day = shiftDateKey(date);
  return publicHolidayDates.some((holiday) => shiftDateKey(holiday) === day);
}

export function expectedForDate(
  counts: ScheduleDayCounts,
  date: string,
  publicHolidayDates: string[]
): number {
  if (isBankHolidayDate(date, publicHolidayDates)) return counts.bankHoliday;
  const parsed = new Date(`${shiftDateKey(date)}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return 0;
  return counts[WEEKDAY_KEYS[parsed.getDay()]] || 0;
}

export type ExpectedCountShiftLike = {
  employeeId: number;
  locationId: number;
  scheduleId: number;
  startTime: string;
};

export type ExpectedCountViolation = {
  messages: string[];
};

function isScheduleLocationShift(
  shift: ExpectedCountShiftLike,
  scheduleId: number,
  locationId: number
): boolean {
  return Number(shift.scheduleId) === Number(scheduleId) && Number(shift.locationId) === Number(locationId);
}

export function countScheduleLocationShiftsOnDate(
  shifts: ExpectedCountShiftLike[],
  scheduleId: number,
  locationId: number,
  date: string
): number {
  return shifts.filter(
    (shift) => isScheduleLocationShift(shift, scheduleId, locationId) && shiftDateKey(shift.startTime) === date
  ).length;
}

export function countScheduleLocationShiftsInRange(
  shifts: ExpectedCountShiftLike[],
  scheduleId: number,
  locationId: number,
  fromDate: string,
  toDate: string
): number {
  return shifts.filter((shift) => {
    if (!isScheduleLocationShift(shift, scheduleId, locationId)) return false;
    const date = shiftDateKey(shift.startTime);
    return date >= fromDate && date <= toDate;
  }).length;
}

function eachDate(weekStart: string, weekEnd: string): string[] {
  const dates: string[] = [];
  for (let date = weekStart; date <= weekEnd; ) {
    dates.push(date);
    const next = new Date(`${date}T00:00:00`);
    next.setDate(next.getDate() + 1);
    date = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
  }
  return dates;
}

export function evaluateShiftCreateExpectedCounts(args: {
  week: ScheduleExpectedCountWeek | null;
  shifts: ExpectedCountShiftLike[];
  scheduleId: number;
  locationId: number;
  shiftDate: string;
}): ExpectedCountViolation | null {
  if (!args.week || Number(args.week.scheduleId) !== Number(args.scheduleId)) return null;
  const expected = expectedForDate(args.week.effective, args.shiftDate, args.week.publicHolidayDates);
  if (expected <= 0) return null;

  const dayCount =
    countScheduleLocationShiftsOnDate(args.shifts, args.scheduleId, args.locationId, args.shiftDate) + 1;
  if (dayCount <= expected) return null;
  return {
    messages: [`This day would have ${dayCount} shifts (expected ${expected}).`],
  };
}

export type ExpectedCountProgress = {
  mode: ExpectedCountMode;
  weekShifts: { actual: number; expected: number };
  dayShifts: { date: string; actual: number; expected: number; isBankHoliday: boolean }[];
};

export function summarizeCurrentWeekExpectedCounts(args: {
  week: ScheduleExpectedCountWeek | null;
  shifts: ExpectedCountShiftLike[];
  scheduleId: number;
  locationId: number;
  weekStart: string;
  weekEnd: string;
}): ExpectedCountProgress | null {
  if (!args.week || Number(args.week.scheduleId) !== Number(args.scheduleId)) return null;

  const dates = eachDate(args.weekStart, args.weekEnd);
  const dayShifts = dates.map((date) => {
    const isBh = isBankHolidayDate(date, args.week!.publicHolidayDates);
    return {
      date,
      actual: countScheduleLocationShiftsOnDate(args.shifts, args.scheduleId, args.locationId, date),
      expected: expectedForDate(args.week!.effective, date, args.week!.publicHolidayDates),
      isBankHoliday: isBh,
    };
  });

  return {
    mode: args.week.mode,
    weekShifts: {
      actual: countScheduleLocationShiftsInRange(
        args.shifts,
        args.scheduleId,
        args.locationId,
        args.weekStart,
        args.weekEnd
      ),
      expected: dayShifts.reduce((sum, day) => sum + day.expected, 0),
    },
    dayShifts,
  };
}

export function evaluateCurrentWeekExpectedCounts(args: {
  week: ScheduleExpectedCountWeek | null;
  shifts: ExpectedCountShiftLike[];
  scheduleId: number;
  locationId: number;
  weekStart: string;
  weekEnd: string;
}): ExpectedCountViolation | null {
  const summary = summarizeCurrentWeekExpectedCounts(args);
  if (!summary) return null;

  const messages: string[] = [];
  for (const day of summary.dayShifts) {
    if (day.expected > 0 && day.actual >= day.expected) {
      const label = day.isBankHoliday ? `${day.date} (BH)` : day.date;
      messages.push(`${label} has ${day.actual} shifts (expected ${day.expected}).`);
    }
  }
  if (summary.weekShifts.expected > 0 && summary.weekShifts.actual >= summary.weekShifts.expected) {
    messages.push(
      `This week has ${summary.weekShifts.actual} shifts (expected ${summary.weekShifts.expected}).`
    );
  }
  if (messages.length === 0) return null;
  return { messages };
}
